import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { settingsApi } from '@/services/settings'
import type {
  ListQuery,
  ServiceCategoryRequest,
  ServiceItemRequest,
  UpdateServiceItemRequest,
  TaxRateRequest,
  SchedulingSettingsRequest,
  PublicHolidayRequest,
  DocumentTemplateRequest,
  ReusableDocumentRequest,
  RenameReusableDocumentRequest,
  AttachmentRuleRequest,
  QuoteSettingsRequest,
  InvoiceSettingsRequest,
  CompanySettingsRequest,
  BrandingRequest,
} from '@/types'
import type { TemplateDocumentType } from '@/lib/documentType'
import type { CreateOptionalFeeTemplateRequest } from '@/types/billing'
import { PICKER_LIMIT } from '@/lib/listFetch'

const KEY = 'settings'
export function useOptionalFees() {
  return useQuery({
    queryKey: [KEY, 'optional-fees'],
    queryFn: settingsApi.listOptionalFees,
  })
}

export function useOptionalFeeMutations() {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY, 'optional-fees'] })
    qc.invalidateQueries({ queryKey: ['quotes', 'optional-fee-templates'] })
  }
  const create = useMutation({
    mutationFn: settingsApi.createOptionalFee,
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body: Partial<CreateOptionalFeeTemplateRequest>
    }) => settingsApi.updateOptionalFee(id, body),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: settingsApi.deleteOptionalFee,
    onSuccess: invalidate,
  })
  return { create, update, remove }
}
export const brandingQueryOptions = {
  queryKey: [KEY, 'branding'] as const,
  queryFn: () => settingsApi.getBranding(),
}

// Generic invalidator helper.
function useInvalidate(scope: string) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: [KEY, scope] })
}

// --- 2.11.5 Service categories ---
export function useServiceCategories(
  q: ListQuery & { is_active?: boolean } = {}
) {
  return useQuery({
    queryKey: [KEY, 'service-categories', q],
    queryFn: () => settingsApi.listServiceCategories(q),
  })
}
export function useCreateServiceCategory() {
  const invalidate = useInvalidate('service-categories')
  return useMutation({
    mutationFn: (body: ServiceCategoryRequest) =>
      settingsApi.createServiceCategory(body),
    onSuccess: invalidate,
  })
}
export function useUpdateServiceCategory() {
  const invalidate = useInvalidate('service-categories')
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ServiceCategoryRequest }) =>
      settingsApi.updateServiceCategory(id, body),
    onSuccess: invalidate,
  })
}
export function useDeleteServiceCategory() {
  const invalidate = useInvalidate('service-categories')
  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteServiceCategory(id),
    onSuccess: invalidate,
  })
}

// --- 2.11.6 Service items ---
export function useServiceItems(
  q: ListQuery & {
    service_category_id?: string
    service_category_ids?: string[]
    is_active?: boolean
    paginate_by_category?: boolean
  } = {}
) {
  return useQuery({
    queryKey: [KEY, 'service-items', q],
    queryFn: () => settingsApi.listServiceItems(q),
  })
}

const SERVICE_ITEM_CATALOG_QUERY = {
  limit: 100,
  is_active: true,
} as const

/**
 * Paginated catalogue used by the service-item library. Search and category
 * stay in the query key so each server-filtered result set owns its own cache.
 */
export function useInfiniteServiceItemCatalog(
  search = '',
  serviceCategoryId?: string
) {
  const normalizedSearch = search.trim()
  const normalizedCategoryId = serviceCategoryId?.trim() || undefined

  return useInfiniteQuery({
    queryKey: [
      KEY,
      'service-items',
      'infinite-catalog',
      normalizedSearch,
      normalizedCategoryId ?? 'all',
    ],
    queryFn: ({ pageParam }) =>
      settingsApi.listServiceItems({
        ...SERVICE_ITEM_CATALOG_QUERY,
        limit: PICKER_LIMIT,
        page: pageParam,
        search: normalizedSearch || undefined,
        service_category_id: normalizedCategoryId,
        paginate_by_category: true,
      }),
    initialPageParam: 1,
    getNextPageParam: last => {
      const { current_page, last_page } = last.metadata
      return current_page < last_page ? current_page + 1 : undefined
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Shared non-paginated catalogue used by Quote and Invoice line-item pickers.
 * Keeping one warm query prevents the library drawer from refetching and
 * regenerating signed image URLs every time it is opened.
 */
export function useServiceItemCatalog() {
  return useQuery({
    queryKey: [KEY, 'service-items', SERVICE_ITEM_CATALOG_QUERY],
    queryFn: () => settingsApi.listServiceItems(SERVICE_ITEM_CATALOG_QUERY),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
// BR02 header counters. Shares the 'service-items' key prefix so it is
// auto-invalidated by the create/update/delete mutations below.
export function useServiceItemsSummary() {
  return useQuery({
    queryKey: [KEY, 'service-items', 'summary'],
    queryFn: () => settingsApi.serviceItemsSummary(),
  })
}
export function useCreateServiceItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ServiceItemRequest) =>
      settingsApi.createServiceItem(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'service-items'] })
      qc.invalidateQueries({ queryKey: [KEY, 'service-categories'] })
    },
  })
}
export function useUpdateServiceItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body: UpdateServiceItemRequest
    }) => settingsApi.updateServiceItem(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'service-items'] })
      qc.invalidateQueries({ queryKey: [KEY, 'service-categories'] })
    },
  })
}
export function useDeleteServiceItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteServiceItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'service-items'] })
      qc.invalidateQueries({ queryKey: [KEY, 'service-categories'] })
    },
  })
}

// --- 2.11.8 Tax rates ---
export function useTaxRates(q: ListQuery = {}) {
  return useQuery({
    queryKey: [KEY, 'tax-rates', q],
    queryFn: () => settingsApi.listTaxRates(q),
  })
}
// BR05 active tax-rate options for line-item dropdowns. Available to any
// authenticated role (operators build quotes/invoices).
export function useTaxRateOptions() {
  return useQuery({
    queryKey: [KEY, 'tax-rates', 'options'],
    queryFn: () => settingsApi.taxRateOptions(),
  })
}
export function useCreateTaxRate() {
  const invalidate = useInvalidate('tax-rates')
  return useMutation({
    mutationFn: (body: TaxRateRequest) => settingsApi.createTaxRate(body),
    onSuccess: invalidate,
  })
}
export function useUpdateTaxRate() {
  const invalidate = useInvalidate('tax-rates')
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TaxRateRequest }) =>
      settingsApi.updateTaxRate(id, body),
    onSuccess: invalidate,
  })
}
export function useDeleteTaxRate() {
  const invalidate = useInvalidate('tax-rates')
  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteTaxRate(id),
    onSuccess: invalidate,
  })
}

// --- 2.11.9 + 2.11.11 Calendar & scheduling config ---
export function useScheduling() {
  return useQuery({
    queryKey: [KEY, 'scheduling'],
    queryFn: () => settingsApi.getScheduling(),
  })
}
export function useUpdateScheduling() {
  const invalidate = useInvalidate('scheduling')
  return useMutation({
    mutationFn: (body: SchedulingSettingsRequest) =>
      settingsApi.updateScheduling(body),
    onSuccess: invalidate,
  })
}

// --- 2.11.10 Public holidays ---
export function usePublicHolidays(q: ListQuery = {}) {
  return useQuery({
    queryKey: [KEY, 'public-holidays', q],
    queryFn: () => settingsApi.listPublicHolidays(q),
  })
}
export function useCreatePublicHoliday() {
  const invalidate = useInvalidate('public-holidays')
  return useMutation({
    mutationFn: (body: PublicHolidayRequest) =>
      settingsApi.createPublicHoliday(body),
    onSuccess: invalidate,
  })
}
export function useUpdatePublicHoliday() {
  const invalidate = useInvalidate('public-holidays')
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PublicHolidayRequest }) =>
      settingsApi.updatePublicHoliday(id, body),
    onSuccess: invalidate,
  })
}
export function useDeletePublicHoliday() {
  const invalidate = useInvalidate('public-holidays')
  return useMutation({
    mutationFn: (id: string) => settingsApi.deletePublicHoliday(id),
    onSuccess: invalidate,
  })
}

// --- 2.11.12 Quote settings ---
export function useQuoteSettings() {
  return useQuery({
    queryKey: [KEY, 'quote'],
    queryFn: () => settingsApi.getQuoteSettings(),
  })
}
export function useUpdateQuoteSettings() {
  const invalidate = useInvalidate('quote')
  return useMutation({
    mutationFn: (body: QuoteSettingsRequest) =>
      settingsApi.updateQuoteSettings(body),
    onSuccess: invalidate,
  })
}

// --- 2.11.13 Invoice settings ---
export function useInvoiceSettings() {
  return useQuery({
    queryKey: [KEY, 'invoice'],
    queryFn: () => settingsApi.getInvoiceSettings(),
  })
}
export function useUpdateInvoiceSettings() {
  const invalidate = useInvalidate('invoice')
  return useMutation({
    mutationFn: (body: InvoiceSettingsRequest) =>
      settingsApi.updateInvoiceSettings(body),
    onSuccess: invalidate,
  })
}

// --- 2.11.14-18 Document templates ---
export function useDocumentTemplates(
  q: ListQuery & { doc_type?: TemplateDocumentType } = {}
) {
  return useQuery({
    queryKey: [KEY, 'templates', q],
    queryFn: () => settingsApi.listDocumentTemplates(q),
  })
}
export function useCreateDocumentTemplate() {
  const invalidate = useInvalidate('templates')
  return useMutation({
    mutationFn: (body: DocumentTemplateRequest) =>
      settingsApi.createDocumentTemplate(body),
    onSuccess: invalidate,
  })
}
export function useUpdateDocumentTemplate() {
  const invalidate = useInvalidate('templates')
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DocumentTemplateRequest }) =>
      settingsApi.updateDocumentTemplate(id, body),
    onSuccess: invalidate,
  })
}
export function useDeleteDocumentTemplate() {
  const invalidate = useInvalidate('templates')
  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteDocumentTemplate(id),
    onSuccess: invalidate,
  })
}
export function useTemplatePlaceholders(docType: TemplateDocumentType) {
  return useQuery({
    queryKey: [KEY, 'template-placeholders', docType],
    queryFn: () => settingsApi.getTemplatePlaceholders(docType),
    staleTime: 5 * 60 * 1000,
  })
}

// --- 2.11.19 Reusable documents ---
export function useReusableDocuments(q: ListQuery = {}) {
  return useQuery({
    queryKey: [KEY, 'document-library', q],
    queryFn: () => settingsApi.listReusableDocuments(q),
  })
}
export function useCreateReusableDocument() {
  const invalidate = useInvalidate('document-library')
  return useMutation({
    mutationFn: (body: ReusableDocumentRequest) =>
      settingsApi.createReusableDocument(body),
    onSuccess: invalidate,
  })
}
export function useUpdateReusableDocument() {
  const invalidate = useInvalidate('document-library')
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body: Partial<ReusableDocumentRequest>
    }) => settingsApi.updateReusableDocument(id, body),
    onSuccess: invalidate,
  })
}
export function useRenameReusableDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body: RenameReusableDocumentRequest
    }) => settingsApi.renameReusableDocument(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'document-library'] })
      qc.invalidateQueries({ queryKey: ['reusable-documents'] })
    },
  })
}
export function useDeleteReusableDocument() {
  const invalidate = useInvalidate('document-library')
  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteReusableDocument(id),
    onSuccess: invalidate,
  })
}

// --- 2.11.20 Attachment rules ---
export function useAttachmentRules(q: ListQuery = {}) {
  return useQuery({
    queryKey: [KEY, 'attachment-rules', q],
    queryFn: () => settingsApi.listAttachmentRules(q),
  })
}
function useInvalidateAttachmentRules() {
  const qc = useQueryClient()
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: [KEY, 'attachment-rules'] }),
      qc.invalidateQueries({
        queryKey: [KEY, 'document-library', 'options'],
      }),
    ])
}
export function useCreateAttachmentRule() {
  const invalidate = useInvalidateAttachmentRules()
  return useMutation({
    mutationFn: (body: AttachmentRuleRequest) =>
      settingsApi.createAttachmentRule(body),
    onSuccess: invalidate,
  })
}
export function useUpdateAttachmentRule() {
  const invalidate = useInvalidateAttachmentRules()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AttachmentRuleRequest }) =>
      settingsApi.updateAttachmentRule(id, body),
    onSuccess: invalidate,
  })
}
export function useDeleteAttachmentRule() {
  const invalidate = useInvalidateAttachmentRules()
  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteAttachmentRule(id),
    onSuccess: invalidate,
  })
}

// --- 2.11.21 Company settings ---
export function useCompanySettings() {
  return useQuery({
    queryKey: [KEY, 'company'],
    queryFn: () => settingsApi.getCompanySettings(),
  })
}
export function useUpdateCompanySettings() {
  const invalidate = useInvalidate('company')
  return useMutation({
    mutationFn: (body: CompanySettingsRequest) =>
      settingsApi.updateCompanySettings(body),
    onSuccess: invalidate,
  })
}

// --- 2.11.22 Branding ---
export function useBranding() {
  return useQuery(brandingQueryOptions)
}
export function useUpdateBranding() {
  const invalidate = useInvalidate('branding')
  return useMutation({
    mutationFn: (body: BrandingRequest) => settingsApi.updateBranding(body),
    onSuccess: invalidate,
  })
}
